import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import { getAccessToken } from '../../utils/cookie.utils';

export type GDriveFolder = {
    id: string;
    name: string;
    mimeType: string | null;
    folderCount: number | null;
    fileCount: number | null;
    fullPath?: string;
    parentId?: string;
    folders?: GDriveFolder[];
    files?: GDriveFile[];
};

export type GDriveFile = {
    id: string;
    name: string;
    mimeType: string;
};

export type AuthStatusResponse = {
    authenticated: boolean;
};

export type AuthUrlResponse = {
    url: string;
};

export type OAuthCallbackResponse = {
    message?: string;
    userId?: string;
    accessToken: string;
    expiresAt: number;
};

export type FoldersResponse = {
    folders: GDriveFolder[];
    files: GDriveFile[];
    folderCount: number | null;
    fileCount: number | null;
    id: string;
    name: string | null;
    fullPath: string;
    parentId: string | null;
    mimeType: string | null;
};

export type SyncProgressEvent = {
    type: 'progress' | 'complete' | 'error';
    current?: number;
    total?: number;
    fileName?: string;
    message?: string;
    requiresReauth?: boolean;
};

class GoogleDriveService {
        /**
         * Disconnect the Google Drive folder for a project (set folderId to null)
         */
        async disconnectProjectFolder(projectId: string): Promise<void> {
            try {
                await apiClient.put(
                    ApiUrls.setProjectGdriveFolder(projectId, ''), // send 'null' string for folderId
                );
            } catch (error: any) {
                throw this.handleError(error);
            }
        }
    /**
     * Check if user is authenticated with Google Drive
     */
    async checkAuthStatus(): Promise<AuthStatusResponse> {
        try {
            const response = await apiClient.get<AuthStatusResponse>(ApiUrls.gdriveAuthStatus);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get Google OAuth authorization URL
     */
    async getAuthUrl(redirectUrl: string): Promise<AuthUrlResponse> {
        try {
            const response = await apiClient.get<AuthUrlResponse>(
                ApiUrls.gdriveAuthUrl(redirectUrl)
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Complete OAuth flow by exchanging code for tokens
     */
    async completeOAuthCallback(code: string, redirectUrl: string): Promise<OAuthCallbackResponse> {
        try {
            const response = await apiClient.post<OAuthCallbackResponse>(
                ApiUrls.gdriveOAuthCallback(code, redirectUrl)
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get a fresh access token using stored refresh token.
     * Avoids re-login when opening Google Picker.
     */
    async getFreshAccessToken(): Promise<{ accessToken: string; expiresAt: number }> {
        try {
            const response = await apiClient.get<{ accessToken: string; expiresAt: number }>(
                ApiUrls.gdriveGetToken
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get folders from Google Drive
     * @param folderId - The folder ID to fetch, or 'root' for the root folder
     */
    async getFolders(folderId: string = 'root'): Promise<FoldersResponse> {
        try {
            const response = await apiClient.get<FoldersResponse>(
                ApiUrls.gdriveFolders(folderId)
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Set the Google Drive folder for a project
     */
    async setProjectFolder(projectId: string, folderId: string): Promise<void> {
        try {
            await apiClient.put(
                ApiUrls.setProjectGdriveFolder(projectId, folderId)
            );
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Trigger sync for project's Google Drive folder (fire-and-forget)
     */
    syncProjectFolder(projectId: string): void {
        // Fire and forget - don't wait for completion, don't handle errors
        apiClient.put(
            ApiUrls.syncProjectGdriveFolder(projectId),
            {},
            {
                timeout: 0, // No timeout
            }
        ).catch((error) => {
            console.error('Sync request failed:', error);
            // Silently fail - polling will handle status
        });
    }

    /**
     * Get sync progress for project's Google Drive folder
     * @returns Object with progress string in format "current/total" or null if no sync in progress
     */
    async getSyncProgress(projectId: string): Promise<{ progress: string | null; totalSynced: number }> {
        try {
            const response = await apiClient.get<{ progress: string | null; totalSynced: number }>(
                ApiUrls.gdriveSyncProgress(projectId)
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Sync folder files with streaming progress updates
     * Uses SSE (Server-Sent Events) for real-time progress
     * 
     * @param projectId - The project ID to sync files to
     * @param folderId - The Google Drive folder ID (empty string if files selected directly)
     * @param folderName - The folder name or description for display
     * @param accessToken - The Google OAuth access token
     * @param files - Array of files to sync (selected via Picker)
     * @param onProgress - Callback for progress updates
     * @param onComplete - Callback when sync completes
     * @param onError - Callback for errors
     * @returns Cleanup function to abort the sync
     */
    syncFolderWithToken(
        projectId: string,
        folderId: string,
        folderName: string,
        accessToken: string,
        files: Array<{ id: string; name: string; mimeType: string; size?: number }>,
        onProgress: (data: { current: number; total: number; fileName: string }) => void,
        onComplete: () => void,
        onError: (error: { message: string; requiresReauth: boolean }) => void
    ): () => void {
        const controller = new AbortController();
        const url = ApiUrls.syncProjectGdriveStream(projectId);

        // Get auth token for backend API calls
        let authToken = typeof window !== 'undefined' ? (window as any)['appAccessToken'] : null;
        if (!authToken) {
            authToken = getAccessToken();
        }

        // Use SSE streaming - backend will download files using the access token
        // With drive.file scope, files selected via Picker are accessible to the app
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ folderId, folderName, accessToken, files }),
            signal: controller.signal,
        })
            .then(async (response) => {
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    onError({
                        message: errorData.message || 'Failed to start sync',
                        requiresReauth: response.status === 401,
                    });
                    return;
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    onError({ message: 'Failed to read response stream', requiresReauth: false });
                    return;
                }

                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    console.log('📦 [SSE] Received chunk:', chunk);
                    buffer += chunk;
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    console.log('📝 [SSE] Processing lines:', lines);
                    for (const line of lines) {
                        console.log('📄 [SSE] Line:', line);
                        if (line.startsWith('data:')) {
                            try {
                                const jsonStr = line.slice(5).trim();
                                console.log('🔍 [SSE] Parsing JSON:', jsonStr);
                                const data: SyncProgressEvent = JSON.parse(jsonStr);
                                console.log('✅ [SSE] Parsed event:', data);

                                if (data.type === 'progress' && data.current !== undefined && data.total !== undefined) {
                                    console.log('📊 [SSE] Calling onProgress with:', { current: data.current, total: data.total, fileName: data.fileName });
                                    onProgress({
                                        current: data.current,
                                        total: data.total,
                                        fileName: data.fileName || '',
                                    });
                                } else if (data.type === 'complete') {
                                    onComplete();
                                } else if (data.type === 'error') {
                                    onError({
                                        message: data.message || 'Sync failed',
                                        requiresReauth: data.requiresReauth || false,
                                    });
                                }
                            } catch (parseError) {
                                console.error('Failed to parse SSE data:', parseError);
                            }
                        }
                    }
                }
            })
            .catch((error) => {
                if (error.name === 'AbortError') {
                    return; // Request was cancelled
                }
                onError({
                    message: error.message || 'Network error during sync',
                    requiresReauth: false,
                });
            });

        // Return cleanup function
        return () => controller.abort();
    }

    /**
     * Handle API errors consistently
     */
    private handleError(error: any): any {
        if (error.response) {
            return {
                message: error.response.data?.message || 'Request failed',
                errors: error.response.data?.errors,
                status: error.response.status,
            };
        }

        // Network error
        return {
            message: 'Network error. Please check your connection.',
            status: 0,
        };
    }
}

export const googleDriveService = new GoogleDriveService();
export default googleDriveService;
